import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createTemplate, getTemplates, getTemplate, updateTemplate, deleteTemplate,
    createGroupClass,
    createSession, getGroupSessions, getGroupSessionDetail, updateGroupSession, completeGroupSession,
    deleteGroupSession,
} from './api';
import { groupSessionKeys } from './keys';
import { groupTicketKeys } from '../groupTicket/keys';
import { groupBookingKeys } from '../groupBooking/keys';
import { CreateTemplateBody, CreateSessionBody, DeleteSessionScope } from './types';

/* ═══════ 수업 템플릿 ═══════ */

export const useTemplates = ({ centerId, enabled = true }: { centerId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: [...groupSessionKeys.all, 'templates', centerId],
        queryFn: () => getTemplates(centerId),
        enabled: enabled && !!centerId,
        select: (data) => data?.data?.results ?? [],
    });

export const useTemplate = ({ centerId, templateId, enabled = true }: { centerId: string; templateId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: [...groupSessionKeys.all, 'template', templateId],
        queryFn: () => getTemplate(centerId, templateId),
        enabled: enabled && !!centerId && !!templateId,
        select: (data) => data?.data?.template ?? null,
    });

export const useCreateTemplate = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: CreateTemplateBody }) => createTemplate(centerId, body),
        onSuccess: () => { qc.invalidateQueries({ queryKey: groupSessionKeys.all }); },
    });
};

export const useUpdateTemplate = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, templateId, body }: { centerId: string; templateId: string; body: Partial<CreateTemplateBody> }) => updateTemplate(centerId, templateId, body),
        onSuccess: () => { qc.invalidateQueries({ queryKey: groupSessionKeys.all }); },
    });
};

export const useDeleteTemplate = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, templateId }: { centerId: string; templateId: string }) => deleteTemplate(centerId, templateId),
        onSuccess: () => { qc.invalidateQueries({ queryKey: groupSessionKeys.all }); },
    });
};

export const useCreateGroupClass = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: Record<string, any> }) => createGroupClass(centerId, body),
        onSuccess: () => { qc.invalidateQueries({ queryKey: groupSessionKeys.all }); },
    });
};

// 하위 호환 alias
export const useGroupClasses = useTemplates;
export const useDeleteGroupClass = useDeleteTemplate;

/* ═══════ 세션 ═══════ */

export const useCreateSession = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: CreateSessionBody }) => createSession(centerId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            // 사전예약 포함 세션 생성은 회원 그룹수강권 회차/예약목록도 바꾸므로 함께 무효화
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
        },
    });
};

export const useGroupSessions = ({ centerId, startDate, endDate, coachId, classType, enabled = true }: { centerId: string; startDate: string; endDate: string; coachId?: string; classType?: string; enabled?: boolean }) =>
    useQuery({
        queryKey: [...groupSessionKeys.list(centerId), startDate, endDate, coachId, classType],
        queryFn: () => {
            const params: Record<string, string> = { startDate, endDate };
            if (coachId) params.coachId = coachId;
            if (classType) params.classType = classType;
            return getGroupSessions(centerId, params);
        },
        enabled: enabled && !!centerId,
        select: (data) => data?.data?.results ?? [],
    });

export const useGroupSessionDetail = ({ centerId, sessionId, enabled = true }: { centerId: string; sessionId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: groupSessionKeys.detail(sessionId),
        queryFn: () => getGroupSessionDetail(centerId, sessionId),
        enabled: enabled && !!centerId && !!sessionId,
        select: (data) => data?.data ?? null,
    });

export const useUpdateGroupSession = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, body }: { centerId: string; sessionId: string; body: any }) => updateGroupSession(centerId, sessionId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
        },
    });
};

export const useCompleteGroupSession = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, body }: { centerId: string; sessionId: string; body: any }) => completeGroupSession(centerId, sessionId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
        },
    });
};

export const useDeleteGroupSession = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, scope }: { centerId: string; sessionId: string; scope?: DeleteSessionScope }) =>
            deleteGroupSession(centerId, sessionId, scope),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            // 세션 삭제는 회원 예약/그룹수강권 회차도 바꾸므로 함께 무효화
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
        },
    });
};
